import { useQuery } from '@tanstack/react-query';
import { useMemo, type ReactNode } from 'react';
import { companyFormat, type CompanyFormat, type CurrencyFormat } from '@workbook/shared';
import { api, type Membership } from '../api/client';
import { getSession } from '../auth/session';
import { CompanyFormatContext } from '../components/company-format';
import { queryClient, queryKeys } from './query';

/**
 * 🔴 THE BACK OFFICE'S COMPANY FORMAT — design-system plan DS-7a (§2).
 *
 * Every `<Money>`, `<Quantity>` and `<DateText>` under the shell reads `CompanyFormatContext`; this is what fills it,
 * from two reads every member can make:
 *
 * - **`/me/memberships`** — the row whose `id` is the session's company carries `baseCurrency`, `timezone` and, since
 *   DS-7a, `grouping` (the company's `documents.grouping`, O-22). The same list the shell's company button and the
 *   "All companies" link already read, under the same key (`queryKeys.memberships`), so it is ONE request.
 * - **`/me/currencies`** — the currency table's symbols and decimals (O-29), fetched once and kept for an hour: a
 *   currency's symbol changes on the order of years.
 *
 * ⚠️ NOT `/company/context` OR `GET /company`: 26 of 30 roles hold neither `settings:read` nor `modules:read`, so most
 * staff — a cashier, a storekeeper — would have been formatted with the defaults (plan §2).
 *
 * 🔬 NOTHING IS VALIDATED HERE. `companyFormat()` fills and repairs what it is given — an unknown grouping becomes lakh,
 * an unknown zone Asia/Dhaka, a NULL BDT symbol `৳` — so a membership row that is late, missing or from an older
 * server still yields a whole format, and a screen never has to ask whether it has one.
 */

/** A `/me/memberships` row as DS-7a's server answers it: `grouping` is the company's `documents.grouping`. */
export type MembershipRow = Membership & { grouping?: string };

/** One row of `GET /me/currencies`. `symbol` is NULL when the table has none (BDT on a fresh install). */
export interface CurrencyRow {
  code: string;
  symbol: string | null;
  decimals: number;
}

/** Not in `queryKeys`, which is shared: installation-wide, so it is not scoped by company. */
const CURRENCIES_KEY = ['me', 'currencies'] as const;

/** 🔬 An hour. The table changes when somebody adds a currency, which is rare enough that a reload is fine. */
const CURRENCIES_STALE_MS = 60 * 60_000;

/**
 * 🔴 THE MEMBERSHIP LIST, ON THE SHARED KEY.
 *
 * ⚠️ `gcTime: 0` — DROPPED THE MOMENT NOTHING IS WATCHING. The key is not scoped by user, and the shell (its only
 * reader) unmounts on sign-out and on a company switch. Kept for the library's default five minutes, the next person
 * to sign in on the same tab would be served the previous person's companies — and their format — until a refetch
 * landed. Dropped, every shell mount asks again, which is what the hand-rolled fetch it replaces did.
 */
export function useMemberships() {
  return useQuery({
    queryKey: queryKeys.memberships(),
    queryFn: () => api.memberships() as Promise<MembershipRow[]>,
    gcTime: 0,
  });
}

/** The format for one membership row and the currency table — pure, so a spec can pin it without a render. */
export function formatFor(
  membership: MembershipRow | undefined,
  currencies: readonly CurrencyRow[] | undefined,
): CompanyFormat {
  const table: Record<string, CurrencyFormat> = {};
  for (const c of currencies ?? []) table[c.code] = { symbol: c.symbol, decimals: c.decimals };
  return companyFormat({
    baseCurrency: membership?.baseCurrency,
    timezone: membership?.timezone,
    // 🔴 O-22: THE COMPANY'S OWN GROUPING. Absent (an older server) or unknown, `companyFormat` picks the default.
    grouping: membership?.grouping as CompanyFormat['grouping'] | undefined,
    currencies: table,
  });
}

/**
 * Mounted around the back-office shell in `routes.tsx`. The session's company is read once per mount: choosing
 * another company unmounts the whole router (`App.tsx`), so this never outlives the company it formats for.
 */
export function CompanyFormatProvider({ children }: { children: ReactNode }) {
  const companyId = getSession()?.companyId ?? null;
  const memberships = useMemberships();
  const currencies = useQuery({
    queryKey: CURRENCIES_KEY,
    queryFn: () => api.get<CurrencyRow[]>('/me/currencies'),
    staleTime: CURRENCIES_STALE_MS,
  });

  const membership = memberships.data?.find((m) => m.id === companyId);
  const value = useMemo(() => formatFor(membership, currencies.data), [membership, currencies.data]);
  return <CompanyFormatContext.Provider value={value}>{children}</CompanyFormatContext.Provider>;
}

/**
 * 🔴 ASK AGAIN AFTER A CHANGE THAT MOVES THE FORMAT — the `documents.grouping` setting (Settings → Preferences) and
 * the company's timezone (Settings → Company). Without it a company that switched to western kept lakh screens until
 * a reload: the divergence DS-7a exists to close, reopened for a minute.
 */
export function refreshCompanyFormat(): Promise<void> {
  return queryClient.invalidateQueries({ queryKey: queryKeys.memberships() });
}
